import { useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { defineMessages } from 'react-intl';
import logTrailDetailsGpxDownloadButtonTapped from '@alltrails/analytics/events/logTrailDetailsGpxDownloadButtonTapped';
import {
  logTrailDetailsOverflowItemSelected,
  logTrailDetailsPrintClicked,
  logTrailDetailsShareClicked,
  logTrailDownloadButtonTapped,
  TrailDetailsOverflowItem
} from 'utils/trail_analytics_helpers';
import useUser from 'hooks/useUser';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { Trail } from 'types/Trails';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import ProgressDialog from '../../shared/ProgressDialog';
import DownloadRouteModal from '../../DownloadRouteModal';
import ProBadge from '../../../../assets/images/icons/pro-badge-white-bg.svg';
import { renderShareModal } from '../../../application/javascripts/react_components/main_action_bar/share_modal';
import TrailPropType from '../../../proptypes/TrailPropType';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { modalSignup } from '../../../utils/modal_funnel_util';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import logError from '../../../utils/logError';
import { wrapEventHandler } from '../../../utils/handlers';
import { copyVerifiedRoute } from '../../../utils/requests/at_map_requests';
import * as styles from './styles/styles.module.scss';

const DROPDOWN_STRINGS = defineMessages({
  DOWNLOAD_ROUTE: { defaultMessage: 'Download Route' },
  CUSTOMIZE_MAP: { defaultMessage: 'Customize Map' },
  SHARE: { defaultMessage: 'Share' },
  PRINT_PDF_MAP: { defaultMessage: 'Print/PDF Map' },
  ALLTRAILS_PRO: { defaultMessage: 'AllTrails Pro' },
  SUGGEST_EDIT: { defaultMessage: 'Suggest Edit' }
});

type ShareObject = {
  id: number;
  name: string;
  slug: string;
  type: 'trail';
  primaryKey: 'trail_id';
};

type Props = {
  exploreFlag: boolean;
  isMobileWidth: boolean;
  mobileBrowser: boolean;
  pending: boolean;
  trail: Trail;
  printUrl: string;
};

const TrailMoreAlternateDropDownList = ({ exploreFlag, isMobileWidth, mobileBrowser, pending, printUrl, trail }: Props) => {
  const {
    formattedDefaultMessages: { DOWNLOAD_ROUTE, CUSTOMIZE_MAP, SHARE, PRINT_PDF_MAP, ALLTRAILS_PRO, SUGGEST_EDIT }
  } = useFormatMessage(DROPDOWN_STRINGS);

  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareObject, setShareObject] = useState<ShareObject>();
  const [progressDialogState, setProgressDialogState] = useState();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadObject, setDownloadObject] = useState<ShareObject>();

  const closeShareModal = () => {
    setShowShareModal(false);
    (document.querySelector('.hide-for-fullscreen-modal') as HTMLElement).style.display = '';
  };

  const handleShareClick = () => {
    logTrailDetailsShareClicked(trail.ID);

    setShowShareModal(true);

    setShareObject({
      id: trail.trail_id,
      name: trail.name,
      slug: trail.slug,
      type: 'trail' as const,
      primaryKey: 'trail_id' as const
    });

    (document.querySelector('.hide-for-fullscreen-modal') as HTMLElement).style.display = 'none';

    return true;
  };

  const handleDeleteTrailClick = () => {
    if (!window.confirm('Are you sure you want to delete this trail?')) {
      return;
    }

    const url = `/api/alltrails/trails/${trail.trail_id}`;

    ServerCommunicationUtil.deleteApiEndpoint(
      url,
      {},
      () => {
        window.location.href = LanguageSupportUtil.wrapUrlSafe('/explore', languageRegionCode);
      },
      (xhr: any, status: any, error: any) => {
        logError(error);
      }
    );
  };

  const handleEditClick = () => {
    const returnToUrl = window.location.pathname + window.location.search;

    logTrailDetailsOverflowItemSelected(trail.ID, TrailDetailsOverflowItem.SuggestTrailEdit);
    if (!user) {
      modalSignup('trail-more-edit', returnToUrl, languageRegionCode);
      return false;
    }

    return true;
  };

  const updateProgressDialog = (state: any) => {
    setProgressDialogState(state);
  };

  const renderProgressDialog = () => {
    if (!progressDialogState) {
      return null;
    }

    return ReactDOM.createPortal(
      <ProgressDialog isMobileWidth={isMobileWidth} variant={progressDialogState} updateProgressDialog={updateProgressDialog} />,
      document.getElementById('modalPortal')
    );
  };

  const handleMapSaveError = (err: any) => {
    updateProgressDialog('error');
    logError(err);
    return err;
  };

  const redirectToMapPage = (map: any) => {
    window.top.location.href = LanguageSupportUtil.wrapUrlSafe(`/explore/map/${map.slug}?save`, languageRegionCode);
  };

  const blockUserNewMapCreation = () => {
    const returnToUrl = window.location.pathname + window.location.search;
    modalSignup('explore-map-new', returnToUrl, languageRegionCode);
  };

  const saveMapAsClone = () => {
    logTrailDetailsOverflowItemSelected(trail.ID, TrailDetailsOverflowItem.CustomizeMap);

    // Block user with signup if not signed-in
    if (!user) {
      blockUserNewMapCreation();
      return;
    }

    const { trail_id, name } = trail;
    updateProgressDialog('saving');
    copyVerifiedRoute(trail_id, name).then(redirectToMapPage).catch(handleMapSaveError);
  };

  const toggleDownloadModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const openDownloadModal = () => {
    logTrailDownloadButtonTapped(trail.trail_id);

    const publishObject = {
      id: trail.trail_id,
      name: trail.name,
      slug: trail.slug,
      type: 'trail' as const,
      primaryKey: 'trail_id' as const
    };

    if (!user) {
      logTrailDetailsGpxDownloadButtonTapped({ trail_id: trail.trail_id });
      const returnToUrl = window.location.pathname + window.location.search;
      modalSignup('trail-more-gpx', returnToUrl, languageRegionCode);
    }

    setShowDownloadModal(true);
    setDownloadObject(publishObject);
  };

  // Non-Pending Items
  let gpxItem = null;
  if (!pending) {
    gpxItem = (
      <div className={styles.dropdownContent}>
        <a data-testid="trail_more_alternate_download_route" onClick={openDownloadModal} title={DOWNLOAD_ROUTE}>
          {DOWNLOAD_ROUTE}
        </a>
      </div>
    );
  }

  const customizeMap = (
    <div className={styles.dropdownContent}>
      <a data-testid="trail_more_alternate_customize_map" key="customizeMap" onClick={wrapEventHandler(saveMapAsClone)}>
        {CUSTOMIZE_MAP}
      </a>
    </div>
  );

  // Explore or Mobile
  let shareItem = null;
  let printUrlItem = null;
  if (exploreFlag || isMobileWidth) {
    shareItem = (
      <div className={styles.dropdownContent}>
        <a data-testid="trail_more_alternate_share" onClick={handleShareClick}>
          {SHARE}{' '}
        </a>
      </div>
    );

    if (!mobileBrowser) {
      printUrlItem = (
        <div className={styles.dropdownContent}>
          <a
            data-testid="trail_more_alternate_print_pdf"
            className={styles.gpxLabel}
            // 2022-06-16 per Sam _blank was removed so we redirect the user to /pro in the same window
            href={printUrl}
            onClick={() => logTrailDetailsPrintClicked(trail.ID)}
          >
            {PRINT_PDF_MAP}
            <span className={styles.printItem}>
              <img alt={ALLTRAILS_PRO} className={styles.proBadge} src={ProBadge} />
            </span>
          </a>
        </div>
      );
    }
  }

  // Requires Signed-in User
  let suggestEditItem = null;
  let deleteTrailItem = null;
  if (user) {
    const suggestUrl = LanguageSupportUtil.wrapUrlSafe(`/${trail.slug}/edit?ref=trail-more-edit`, languageRegionCode);
    // suggest edit display: none for screen sizes under 767 (unless being displayed in trail-explore left panel)
    suggestEditItem = (
      <div className={classNames(styles.dropdownContent, 'suggest', !exploreFlag && 'trail-details')}>
        <a href={suggestUrl} target="_blank" onClick={handleEditClick} data-testid="trail_more_alternate_suggest_edit" rel="noreferrer">
          {SUGGEST_EDIT}
        </a>
      </div>
    );
    // Requires Signed-in Admin User
    if (user.admin) {
      deleteTrailItem = (
        <div className={styles.dropdownContent} onClick={handleDeleteTrailClick}>
          <a data-testid="trail_more_alternate_delete_trail">Delete Trail</a>
        </div>
      );
    }
  }

  return (
    <>
      <div className={styles.dropdown}>
        {suggestEditItem}
        {deleteTrailItem}
        {shareItem}
        {gpxItem}
        {customizeMap}
        {printUrlItem}
      </div>
      {renderShareModal(showShareModal, closeShareModal, shareObject, user)}
      {renderProgressDialog()}
      {user && showDownloadModal && <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} trailSlug={downloadObject?.slug} />}
    </>
  );
};

TrailMoreAlternateDropDownList.propTypes = {
  trail: TrailPropType.isRequired
};

export default TrailMoreAlternateDropDownList;
